import { memo } from 'react'
import { DeepPartial } from 'utility-types'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from 'revamp/reusable-components/tooltip/tooltip'
import InStockL2Heading from '../Pages/InStockDetailPageV2/InL2Heading'
import { Consensus } from './types'
import { spCopyRight, spCopyRightSource } from './constant'
import WhatExpertsSay from '@/stories/components/USStocksDetail/WhatExpertsSay'
import colors from '@ind-money/tailwind/tokens/colors'
import { StencilCDNImage } from '@/components/Common/StencilImage'
import { RUPEE_SYMBOL } from '@/root/constants'

const WhatExpertsSayWrapper = memo(
  ({
    consensus = {},
    company_name = '',
    meta_info = {},
    showSource = true,
  }: {
    consensus: DeepPartial<Consensus> | null
    company_name: string
    meta_info?: {
      currency_symbol?: string
    }
    showSource?: boolean
  }) => {
    let sentimentColor = colors.brand.green.dark
    switch (consensus?.sentiment) {
      case 'HOLD':
        sentimentColor = colors.chart.yellow
        break
      case 'SELL':
        sentimentColor = colors.brand.red.cinnabar
        break
      default:
        break
    }

    const chartBarData: {
      name: string
      color: string
      status: string
      percent: number
    }[] = []

    if (consensus?.buy_per !== undefined) {
      chartBarData.push({
        name: `${consensus.buy_per.toFixed(2)}%`,
        color: colors.brand.green.dark,
        status: 'Buy',
        percent: consensus.buy_per,
      })
    }
    if (consensus?.hold_per !== undefined) {
      chartBarData.push({
        name: `${consensus.hold_per.toFixed(2)}%`,
        color: colors.chart.yellow,
        status: 'Hold',
        percent: consensus.hold_per,
      })
    }
    if (consensus?.sell_per !== undefined) {
      chartBarData.push({
        name: `${consensus.sell_per.toFixed(2)}%`,
        color: colors.brand.red.cinnabar,
        status: 'Sell',
        percent: consensus.sell_per,
      })
    }

    const expertSayTemplate = {
      chartBarData,
      recom: consensus?.sentiment && {
        label: consensus.sentiment,
        bg_color: sentimentColor,
        label_color: '#fff',
      },
      bottom_info: {
        text:
          consensus?.sentiment && consensus.target_prc
            ? `Based on ${consensus.num_analysts} analysts, ${Math.max(
                consensus.buy_per ?? 0,
                consensus.hold_per ?? 0,
                consensus.sell_per ?? 0,
              )}% of analysts recommend a '${
                consensus.sentiment
              }' rating for ${company_name}. Average target price of ${
                meta_info?.currency_symbol ?? RUPEE_SYMBOL
              }${consensus.target_prc}`
            : '',
      },
      footer_text: showSource ? spCopyRightSource : '',
    }

    return (
      <div className="flex flex-col justify-start">
        <div className="flex w-full items-center justify-between">
          {company_name && (
            <InStockL2Heading
              heading={`Analyst Recommendation on ${company_name} Stock`}
              subheading={`Based on ${consensus?.num_analysts} analysts`}
            />
          )}
          {showSource && (
            <Tooltip>
              <TooltipTrigger>
                <StencilCDNImage
                  src="https://indcdn.indmoney.com/public/images/info_new.png"
                  width={18}
                  height={18}
                  onClick={() => {}}
                />
              </TooltipTrigger>
              <TooltipContent>
                <div className="border-1 w-80 bg-brand-white p-2 text-xs shadow-one">
                  {spCopyRight}
                </div>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
        <WhatExpertsSay data={expertSayTemplate} />
      </div>
    )
  },
)

WhatExpertsSayWrapper.displayName = 'WhatExpertsSay'

export default WhatExpertsSayWrapper
